import { zodResolver } from '@hookform/resolvers/zod';
import { CircleAlert } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DataTable } from '@/components/ui/data-table';
import { Input } from '@/components/ui/input';
import { TableSkeleton } from '@/components/ui/skeleton';
import { apiClient } from '@/lib/api-client';
import type { paths } from '@/lib/api-types-clinical-study';
import { readErrorKey } from '@/lib/read-error';
import { useKeyedPendingWrite, usePendingWrite } from '@/lib/use-pending-write';

type TreatmentDatePoolEntry =
  paths['/v1/clinical-study/treatment-date-pool']['get']['responses'][200]['content']['application/json'][number];

type LoadState =
  | { status: 'loading'; entries: [] }
  | { status: 'error'; entries: []; httpStatus: number | undefined }
  | { status: 'ready'; entries: TreatmentDatePoolEntry[] };

const createPoolEntrySchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'La data deve essere nel formato AAAA-MM-GG.'),
  capacity: z.coerce
    .number()
    .int('Deve essere un numero intero.')
    .positive('Deve essere maggiore di zero.'),
});
type CreatePoolEntryFormInput = z.input<typeof createPoolEntrySchema>;
type CreatePoolEntryFormOutput = z.output<typeof createPoolEntrySchema>;

/**
 * `/clinical-study/treatment-date-pool` (FR-9.5(a), Resolution R9): Organization-scoped, NOT
 * study-scoped — list + create + a per-row inline capacity edit. The capacity cell follows this
 * workspace's own documented UNCONTROLLED-cell convention (`products/console-web/docs/
 * project-context.md`): `defaultValue` + a `ref`, read only at save-click time, never a
 * per-keystroke `useState` threaded through `columns`. Reaches `/v1/clinical-study/treatment-date-
 * pool` with NO explicit `params` object (Resolution R2) — the ambient middleware attaches the
 * active-context headers.
 */
export function TreatmentDatePoolPage() {
  const { t } = useTranslation(['clinical-study', 'errors']);
  const [state, setState] = useState<LoadState>({ status: 'loading', entries: [] });
  const [duplicateDateError, setDuplicateDateError] = useState(false);
  const [createApiError, setCreateApiError] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const capacityInputRefs = useRef<Map<string, HTMLInputElement>>(new Map());

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreatePoolEntryFormInput, unknown, CreatePoolEntryFormOutput>({
    resolver: zodResolver(createPoolEntrySchema),
  });
  const { pending, run } = usePendingWrite();
  const { isPending, run: runRow } = useKeyedPendingWrite();

  const load = () => {
    setState({ status: 'loading', entries: [] });
    apiClient
      .GET('/v1/clinical-study/treatment-date-pool')
      .then(({ data, error, response }) => {
        // The status chooses the words (`readErrorKey`); it is never shown itself.
        if (error !== undefined || !response.ok || data === undefined) {
          setState({ status: 'error', entries: [], httpStatus: response.status });
          return;
        }
        setState({ status: 'ready', entries: data });
      })
      .catch(() => setState({ status: 'error', entries: [], httpStatus: undefined }));
  };

  useEffect(load, []);

  const onSubmit = async (values: CreatePoolEntryFormOutput) => {
    setDuplicateDateError(false);
    setCreateApiError(null);
    const { data, error, response } = await apiClient.POST(
      '/v1/clinical-study/treatment-date-pool',
      {
        body: { date: values.date, capacity: values.capacity },
      },
    );

    if (error !== undefined || !response.ok || data === undefined) {
      if (response.status === 409) {
        setDuplicateDateError(true);
      } else {
        setCreateApiError(error?.detail ?? t('clinical-study:writeError'));
      }
      return;
    }

    reset();
    setState((previous) =>
      previous.status === 'ready'
        ? { status: 'ready', entries: [data, ...previous.entries] }
        : previous,
    );
    toast.success(t('clinical-study:pool.createdToast'));
  };

  const onUpdateCapacity = async (entryId: string) => {
    const inputElement = capacityInputRefs.current.get(entryId);
    const capacity = Number(inputElement?.value ?? '');
    const { data, error, response } = await apiClient.PATCH(
      '/v1/clinical-study/treatment-date-pool/{entryId}',
      { params: { path: { entryId } }, body: { capacity } },
    );

    if (error !== undefined || !response.ok || data === undefined) {
      setRowErrors((previous) => ({
        ...previous,
        [entryId]: error?.detail ?? t('clinical-study:pool.capacityTooLowError'),
      }));
      return;
    }

    setRowErrors((previous) => {
      const next = { ...previous };
      delete next[entryId];
      return next;
    });
    setState((previous) =>
      previous.status === 'ready'
        ? {
            status: 'ready',
            entries: previous.entries.map((row) => (row.id === data.id ? data : row)),
          }
        : previous,
    );
    toast.success(t('clinical-study:pool.capacityUpdatedToast'));
  };

  return (
    <div className="space-y-6">
      <Link to="/clinical-study/studies" className="text-sm underline">
        {t('clinical-study:pool.backToStudies')}
      </Link>
      <h2 className="screen-title">{t('clinical-study:pool.title')}</h2>

      <Card>
        <CardHeader>
          <CardTitle>{t('clinical-study:pool.createTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            className="space-y-4"
            onSubmit={(event) => void handleSubmit((values) => run(() => onSubmit(values)))(event)}
          >
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="pool-entry-date">
                {t('clinical-study:pool.dateLabel')}
              </label>
              <Input id="pool-entry-date" type="date" {...register('date')} />
              {errors.date && <p className="text-destructive text-sm">{errors.date.message}</p>}
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium" htmlFor="pool-entry-capacity">
                {t('clinical-study:pool.capacityLabel')}
              </label>
              <Input
                id="pool-entry-capacity"
                type="number"
                min="1"
                step="1"
                {...register('capacity')}
              />
              {errors.capacity && (
                <p className="text-destructive text-sm">{errors.capacity.message}</p>
              )}
            </div>
            {duplicateDateError && (
              <p className="text-destructive text-sm">
                {t('clinical-study:pool.duplicateDateError')}
              </p>
            )}
            {createApiError && <p className="text-destructive text-sm">{createApiError}</p>}
            <Button type="submit" disabled={pending}>
              {t('clinical-study:pool.createSubmit')}
            </Button>
          </form>
        </CardContent>
      </Card>

      {state.status === 'loading' && <TableSkeleton columns={3} />}
      {state.status === 'error' && (
        <div className="space-y-2">
          <p className="text-destructive flex items-center gap-2 text-sm">
            <CircleAlert className="size-5 shrink-0" aria-hidden="true" />
            {t(readErrorKey(state.httpStatus))}
          </p>
          <Button variant="outline" size="sm" onClick={load}>
            {t('clinical-study:pool.retry')}
          </Button>
        </div>
      )}
      {state.status === 'ready' && (
        <DataTable
          columns={[
            { accessorKey: 'date', header: t('clinical-study:pool.dateLabel') },
            {
              id: 'capacity',
              header: t('clinical-study:pool.capacityLabel'),
              cell: ({ row }) => (
                <div className="flex items-start gap-2">
                  <div className="space-y-1">
                    <Input
                      type="number"
                      min="1"
                      step="1"
                      className="w-20"
                      defaultValue={row.original.capacity}
                      ref={(element) => {
                        if (element) {
                          capacityInputRefs.current.set(row.original.id, element);
                        }
                      }}
                    />
                    {rowErrors[row.original.id] && (
                      <p className="text-destructive text-sm">{rowErrors[row.original.id]}</p>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={isPending(row.original.id)}
                    onClick={() =>
                      void runRow(row.original.id, () => onUpdateCapacity(row.original.id))
                    }
                  >
                    {t('clinical-study:pool.editCapacitySubmit')}
                  </Button>
                </div>
              ),
            },
            {
              accessorKey: 'consumedCount',
              header: t('clinical-study:pool.consumedLabel'),
            },
          ]}
          data={state.entries}
          emptyMessage={t('clinical-study:pool.emptyMessage')}
        />
      )}
    </div>
  );
}
